import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { DetailIconComponent } from '@sharedModule/components/detail-icon/detail-icon.component';
import { DocumentPreviewDialogComponent } from '@sharedModule/components/document-preview-dialog/document-preview-dialog.component';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { FormatTextPipe } from '@sharedModule/pipe/format-text/format-text.pipe';
import { StatusPillPipe } from '@sharedModule/pipe/status-pill/status-pill.pipe';
import { Observable, Subject, takeUntil } from 'rxjs';
import { SenderActionsService } from '../../services/sender-actions.service';
import { SenderService } from '../../services/sender.service';
import {
  DocumentInterface,
  SenderListInterface,
} from '../../types/sender-list';
import { SenderRecipientListComponent } from '../sender-recipient-list/sender-recipient-list.component';
import { SenderTransactionListComponent } from '../sender-transaction-list/sender-transaction-list.component';
import { SenderWalletListComponent } from '../sender-wallet-list/sender-wallet-list.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-sender-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MaterialModule,
    HasAccessDirective,
    DetailIconComponent,
    FormatTextPipe,
    StatusPillPipe,
    SenderWalletListComponent,
    SenderRecipientListComponent,
    SenderTransactionListComponent,
  ],
  templateUrl: './sender-details.component.html',
  styleUrl: './sender-details.component.scss',
})
export class SenderDetailsComponent implements OnInit, OnDestroy {
  id!: string;
  senderData?: SenderListInterface;
  isSuperAdmin!: boolean;
  #destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private senderService: SenderService,
    private senderActions: SenderActionsService,
    private authenticationService: AuthenticationService
  ) {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    this.isSuperAdmin = this.authenticationService.isSuperAdmin();
  }

  /** Sender name with the optional middle name collapsed out. */
  get fullName(): string {
    if (!this.senderData) return '';
    return [
      this.senderData.first_name,
      this.senderData.middle_name,
      this.senderData.last_name,
    ]
      .filter(Boolean)
      .join(' ');
  }

  get documentCount(): number {
    return this.senderData?.documents?.length ?? 0;
  }

  ngOnInit(): void {
    this.loadSenderDetails();
  }

  loadSenderDetails(): void {
    const payload = this.isSuperAdmin
      ? {
          sender_id: +this.id.split('-')[0],
          partner_id: +this.id.split('-')[1],
        }
      : { sender_id: +this.id };
    this.senderService
      .getSenderDetails(payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) this.senderData = x.response;
        },
      });
  }

  /**
   * Opens an uploaded document.
   *
   * doc.image is a private GCS link that 403s when followed directly, so the
   * dialog swaps it for a viewable image through partner/getGcsImage.
   */
  viewDocument(document: DocumentInterface): void {
    this.dialog.open(DocumentPreviewDialogComponent, {
      width: '640px',
      data: {
        title: [document.document_type, document.document_section]
          .filter(Boolean)
          .join(' - '),
        gcsImageLink: document.image,
        partnerId: document.partner_id ?? this.senderData?.partner_id,
      },
    });
  }

  toggleActiveStatus(): void {
    if (!this.senderData) return;
    this.handleAction(
      this.senderActions.toggleActiveStatus(
        this.senderData.sender_id,
        this.senderData.is_active
      )
    );
  }

  /**
   * The toggle owns its own checked state, so revert it when the change is
   * cancelled or rejected by the server.
   */
  toggleDeviceVerification(event: MatSlideToggleChange): void {
    if (!this.senderData) return;
    this.senderActions
      .toggleDeviceVerification(this.senderData.sender_id, event.checked)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(success => {
        if (success) this.loadSenderDetails();
        else event.source.checked = !event.checked;
      });
  }

  updateKycStatus(): void {
    if (!this.senderData) return;
    this.handleAction(
      this.senderActions.updateKycStatus(this.senderData.sender_id)
    );
  }

  updateAmlStatus(): void {
    if (!this.senderData) return;
    this.handleAction(
      this.senderActions.updateAmlStatus(this.senderData.sender_id)
    );
  }

  resetPassword(): void {
    if (!this.senderData) return;
    this.handleAction(
      this.senderActions.resetPassword(this.senderData.sender_id)
    );
  }

  resetPin(): void {
    if (!this.senderData) return;
    this.handleAction(this.senderActions.resetPin(this.senderData.sender_id));
  }

  private handleAction(action$: Observable<boolean>): void {
    action$.pipe(takeUntil(this.#destroy$)).subscribe(success => {
      if (success) this.loadSenderDetails();
    });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
